import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Table, message, Button, Tag, Space } from 'antd';
import { UserOutlined, TeamOutlined, FileTextOutlined, ClockCircleOutlined, CheckCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { doctorService } from '../../services';
import appointmentService from '../../services/appointmentService';
import { isOnline, subscribe as onConnectivity } from '../../services/connectivity';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const DoctorDashboard = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [waitingQueue, setWaitingQueue] = useState([]);
    const [scheduledAppointments, setScheduledAppointments] = useState([]);
    const [queueLoading, setQueueLoading] = useState(false);
    const { t } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        fetchData();
        fetchQueue();

        // Auto-refresh queue every 30 seconds - but not while offline, where
        // every tick would just fail. Refresh straight away when the
        // connection comes back instead.
        const interval = setInterval(() => {
            if (isOnline()) fetchQueue();
        }, 30000);
        const unsubscribe = onConnectivity((online) => {
            if (online) fetchQueue();
        });
        return () => {
            clearInterval(interval);
            unsubscribe();
        };
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await doctorService.getDashboard();
            setData(response.data);
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    };

    const fetchQueue = async () => {
        setQueueLoading(true);
        try {
            const [waitingRes, scheduledRes] = await Promise.all([
                appointmentService.getWaitingQueue(),
                appointmentService.getScheduledAppointments()
            ]);
            setWaitingQueue(waitingRes.data);
            setScheduledAppointments(scheduledRes.data);
        } catch (error) {
            console.error('Failed to fetch queue');
        } finally {
            setQueueLoading(false);
        }
    };

    const getWaitingTime = (checkInTime) => {
        if (!checkInTime) return '-';
        const now = new Date();
        const checkedIn = new Date(checkInTime);
        const diffMinutes = Math.floor((now - checkedIn) / 60000);
        return `${diffMinutes} min`;
    };

    const handleStartConsultation = (patientId) => {
        navigate(`/doctor/patient/${patientId}`);
    };

    const patientColumns = [
        {
            title: t('name'),
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: t('phoneNumber'),
            dataIndex: 'phoneNumber',
            key: 'phoneNumber'
        },
        {
            title: t('createdAt'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => dayjs(date).format('YYYY-MM-DD')
        }
    ];

    const visitColumns = [
        {
            title: t('patientDetails'),
            dataIndex: ['patientId', 'name'],
            key: 'patient'
        },
        {
            title: t('visitDate'),
            dataIndex: 'visitDate',
            key: 'visitDate',
            render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm')
        },
        {
            title: t('createdBy'),
            dataIndex: ['createdBy', 'name'],
            key: 'createdBy'
        }
    ];

    const waitingQueueColumns = [
        {
            title: '#',
            key: 'index',
            width: 50,
            render: (_, __, index) => index + 1
        },
        {
            title: 'Patient',
            dataIndex: ['patientId', 'name'],
            key: 'patient',
            render: (name, record) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{name}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        {record.patientId.age} yrs, {record.patientId.gender}
                    </div>
                </div>
            )
        },
        {
            title: 'Scheduled',
            dataIndex: 'appointmentTime',
            key: 'appointmentTime'
        },
        {
            title: 'Arrived At',
            dataIndex: 'checkInTime',
            key: 'checkInTime',
            render: (time) => time ? dayjs(time).format('hh:mm A') : '-'
        },
        {
            title: 'Waiting',
            key: 'waiting',
            render: (_, record) => {
                if (record.status === 'in-consultation') {
                    return <Tag color="purple" icon={<SyncOutlined spin />}>In Consultation</Tag>;
                }
                return <Tag color="orange">{getWaitingTime(record.checkInTime)}</Tag>;
            }
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    onClick={() => handleStartConsultation(record.patientId._id)}
                    style={{
                        background: 'linear-gradient(135deg, #028090 0%, #00A896 100%)',
                        border: 'none'
                    }}
                >
                    {record.status === 'in-consultation' ? 'Continue' : 'Start Consultation'}
                </Button>
            )
        }
    ];

    const scheduledColumns = [
        {
            title: 'Time',
            dataIndex: 'appointmentTime',
            key: 'appointmentTime',
            width: 100
        },
        {
            title: 'Patient',
            dataIndex: ['patientId', 'name'],
            key: 'patient'
        },
        {
            title: 'Phone',
            dataIndex: ['patientId', 'phoneNumber'],
            key: 'phone'
        },
        {
            title: 'Status',
            key: 'status',
            render: () => <Tag color="blue" icon={<ClockCircleOutlined />}>Scheduled</Tag>
        }
    ];

    return (
        <div>
            <h1>{t('dashboard')}</h1>

            {/* Waiting Queue - Priority Display */}
            {waitingQueue.length > 0 && (
                <Card
                    title={
                        <Space>
                            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
                            <span style={{ fontSize: '18px', fontWeight: 600 }}>
                                Patients Waiting ({waitingQueue.length})
                            </span>
                        </Space>
                    }
                    style={{
                        marginBottom: 16,
                        borderRadius: '12px',
                        border: '2px solid #52c41a',
                        boxShadow: '0 4px 12px rgba(82, 196, 26, 0.15)'
                    }}
                    headStyle={{ background: '#f6ffed' }}
                >
                    <Table
                        columns={waitingQueueColumns}
                        dataSource={waitingQueue}
                        rowKey="_id"
                        pagination={false}
                        size="small"
                        loading={queueLoading}
                    />
                </Card>
            )}

            {/* Scheduled Appointments */}
            {scheduledAppointments.length > 0 && (
                <Card
                    title={
                        <Space>
                            <ClockCircleOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
                            <span style={{ fontSize: '18px', fontWeight: 600 }}>
                                Scheduled for Today ({scheduledAppointments.length})
                            </span>
                        </Space>
                    }
                    style={{
                        marginBottom: 16,
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(24, 144, 255, 0.08)'
                    }}
                >
                    <Table
                        columns={scheduledColumns}
                        dataSource={scheduledAppointments}
                        rowKey="_id"
                        pagination={false}
                        size="small"
                        loading={queueLoading}
                    />
                </Card>
            )}

            {/* Statistics */}
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={8}>
                    <Card>
                        <Statistic
                            title={t('totalPatients')}
                            value={data?.statistics?.totalPatients || 0}
                            prefix={<UserOutlined />}
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <Card>
                        <Statistic
                            title={t('totalVisits')}
                            value={data?.statistics?.totalVisits || 0}
                            prefix={<FileTextOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <Card>
                        <Statistic
                            title={t('totalSecretaries')}
                            value={data?.statistics?.totalSecretaries || 0}
                            prefix={<TeamOutlined />}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                <Col xs={24} lg={12}>
                    <Card title={t('recentPatients')}>
                        <Table
                            columns={patientColumns}
                            dataSource={data?.recentPatients || []}
                            rowKey="_id"
                            pagination={{ pageSize: 5, showSizeChanger: false }}
                            size="small"
                            onRow={(record) => ({
                                onClick: () => navigate(`/doctor/patient/${record._id}`)
                            })}
                            style={{ cursor: 'pointer' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card title={t('recentVisits')}>
                        <Table
                            columns={visitColumns}
                            dataSource={data?.recentVisits || []}
                            rowKey="_id"
                            pagination={{ pageSize: 5, showSizeChanger: false }}
                            size="small"
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default DoctorDashboard;
